import {
  AddLink,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined
} from '@mui/icons-material'
import {
  Box,
  BoxProps,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import React, { KeyboardEvent } from 'react'
import { connect } from 'react-redux'
import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  DraftBlockType,
  DraftHandleValue,
  Editor,
  EditorCommand,
  EditorState,
  RichUtils,
  convertFromRaw,
  convertToRaw,
  getDefaultKeyBinding
} from 'draft-js'
import 'draft-js/dist/Draft.css'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import { mapOperationsToDispatchProps } from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
  }
}

interface OwnProps {
  rteState: string
  setRteState: (raw: string) => void
  setPLainState?: (plain: string) => void
  readOnly?: boolean
  containerProps?: BoxProps
  error?: boolean
  reStater?: string
}

type AppProps = StateProps & DispatchProps & OwnProps

const NivosRTEFC: React.FC<any> = (props: AppProps) => {
  const {
    theme,
    rteState,
    setRteState,
    setPLainState,
    readOnly,
    containerProps,
    error,
    reStater
  } = props
  const [urlPromptOpen, setPromptOpen] = React.useState(false)
  const [urlVal, setUrlVal] = React.useState<null | string>()

  const findLinkEntities = (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentState
  ): void => {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity()
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'LINK'
      )
    }, callback)
  }

  const URLDialog: React.FC<{ url?: string }> = props => {
    const [urlValue, setUrlValue] = React.useState(
      props.url !== undefined ? props.url : ''
    )

    const confirmURL = (): void => {
      const contentState = editorState.getCurrentContent()
      const contentStateWithEntity = contentState.createEntity(
        'LINK',
        'MUTABLE',
        { url: urlValue }
      )
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity
      })
      setEditorState(
        RichUtils.toggleLink(
          newEditorState,
          newEditorState.getSelection(),
          entityKey
        )
      )
      saveRTEStates()
    }

    return (
      <Dialog open={urlPromptOpen} onClose={() => setPromptOpen(false)}>
        <DialogTitle>Link URL</DialogTitle>
        <DialogContent>
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
          >
            <Typography fontSize={'smaller'}>
              Make sure to include <strong>https://</strong> in URL
            </Typography>
            <TextField
              autoFocus
              type={'url'}
              sx={{ marginY: 2 }}
              value={urlValue}
              onChange={event => {
                setUrlValue(event.currentTarget.value)
              }}
            />
            <Button
              variant={'contained'}
              onClick={() => {
                confirmURL()
                setPromptOpen(false)
              }}
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  const Link = (p: any): any => {
    const { url } = p.contentState.getEntity(p.entityKey).getData()
    return (
      <a href={url} target={'_blank'} rel={'noreferrer'}>
        {p.children}
      </a>
    )
  }

  const decorator = new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: Link
    }
  ])

  const [editorState, setEditorState] = React.useState(() =>
    EditorState.createEmpty(decorator)
  )

  const handleKeyCommand = (command: EditorCommand): DraftHandleValue => {
    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState !== null) {
      setEditorState(newState)
      return 'handled'
    }

    return 'not-handled'
  }

  const toggleBlockType = (type: DraftBlockType): void => {
    setEditorState(RichUtils.toggleBlockType(editorState, type))
  }

  const mapKeyToEditorCommand = (event: KeyboardEvent): null | string => {
    if (event.key === 'Tab') {
      const newState = RichUtils.onTab(event, editorState, 4)
      event.preventDefault()
      if (newState !== editorState) {
        setEditorState(newState)
      }

      return null
    }

    return getDefaultKeyBinding(event)
  }

  const promptLinkInput = (): void => {
    const selection = editorState.getSelection()
    const collapsed: boolean = selection.isCollapsed()
    setUrlVal(null)
    if (!collapsed) {
      const contentState = editorState.getCurrentContent()
      const startKey = selection.getStartKey()
      const startOffset = selection.getStartOffset()
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey)
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset)
      if (linkKey !== null) {
        const linkKeyInstance = contentState.getEntity(linkKey)
        const url = linkKeyInstance.getData().url
        setUrlVal(url)
      }
    }

    setPromptOpen(true)
  }

  const saveRTEStates = (): void => {
    if (!editorState.getCurrentContent().hasText()) return
    const currentContent = editorState.getCurrentContent()
    setRteState(JSON.stringify(convertToRaw(currentContent)))
    if (setPLainState !== undefined) {
      setPLainState(currentContent.getPlainText())
    }
  }

  const setEditorStateFromString = (
    rteString: string,
    hasText = false
  ): void => {
    try {
      if (
        hasText &&
        rteString.trim() !== '' &&
        editorState.getCurrentContent().hasText()
      ) {
        setEditorState(
          EditorState.createWithContent(
            convertFromRaw(JSON.parse(rteString)),
            decorator
          )
        )
      } else if (
        rteString.trim() !== '' &&
        !editorState.getCurrentContent().hasText()
      ) {
        setEditorState(
          EditorState.createWithContent(
            convertFromRaw(JSON.parse(rteString)),
            decorator
          )
        )
      }
    } catch (e) {
      setEditorState(EditorState.createEmpty(decorator))
    }
  }

  React.useEffect(() => {
    // Try to load rteState into editorState
    setEditorStateFromString(rteState)
  }, [rteState])

  React.useEffect(() => {
    // This lets parent component to force-reset the editor-state without causing infinite-state-loop
    if (reStater !== undefined) setEditorStateFromString(reStater, true)
  }, [reStater])

  React.useEffect(() => saveRTEStates(), [editorState])

  return (
    <Box
      padding={(readOnly !== undefined ? !readOnly : true) ? 2 : 0}
      borderRadius={'12px'}
      border={
        (readOnly !== undefined ? !readOnly : true)
          ? `2px solid ${
              (error !== undefined ? error : false) ? 'red' : '#d4d2d2'
            }`
          : 'none'
      }
    >
      {(readOnly !== undefined ? !readOnly : true) && (
        <>
          <URLDialog url={urlVal !== null ? urlVal : undefined} />
          <Box paddingBottom={1} display={'flex'} alignItems={'center'}>
            <Tooltip title={'Bold'} placement={'top'}>
              <IconButton
                onClick={() => handleKeyCommand('bold')}
                sx={{ marginRight: 1 }}
              >
                <FormatBold />
              </IconButton>
            </Tooltip>
            <Tooltip title={'Italic'} placement={'top'}>
              <IconButton
                onClick={() => handleKeyCommand('italic')}
                sx={{ marginRight: 1 }}
              >
                <FormatItalic />
              </IconButton>
            </Tooltip>
            <Tooltip title={'Underline'} placement={'top'}>
              <IconButton
                onClick={() => handleKeyCommand('underline')}
                sx={{ marginRight: 1 }}
              >
                <FormatUnderlined />
              </IconButton>
            </Tooltip>
            <Tooltip title={'Make un-ordered list'} placement={'top'}>
              <IconButton
                onClick={() => toggleBlockType('unordered-list-item')}
                sx={{ marginRight: 1 }}
              >
                <FormatListBulleted />
              </IconButton>
            </Tooltip>
            <Tooltip title={'Make ordered list'} placement={'top'}>
              <IconButton
                onClick={() => toggleBlockType('ordered-list-item')}
                sx={{ marginRight: 1 }}
              >
                <FormatListNumbered />
              </IconButton>
            </Tooltip>
            <Tooltip title={'Add link to text'} placement={'top'}>
              <IconButton
                onClick={() => promptLinkInput()}
                sx={{ marginRight: 1 }}
              >
                <AddLink />
              </IconButton>
            </Tooltip>
          </Box>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              color: theme.secondaryColor,
              borderRadius: 0,
              borderTop: 2,
              borderColor: '#d4d2d2'
            }}
          >
            <Box className="RichEditor-root" padding={1} {...containerProps}>
              <Editor
                editorState={editorState}
                onChange={setEditorState}
                handleKeyCommand={handleKeyCommand}
                keyBindingFn={mapKeyToEditorCommand}
              />
            </Box>
          </Paper>
        </>
      )}
      {(readOnly !== undefined ? readOnly : false) && (
        <Box
          borderRadius={'inherit'}
          className="RichEditor-root"
          color={theme.secondaryColor}
          {...containerProps}
        >
          <Editor
            editorState={editorState}
            onChange={setEditorState}
            readOnly={true}
          />
        </Box>
      )}
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations
})

const NivosRTE = connect(
  mapStateToProps,
  mapDispatchToProps
)(NivosRTEFC) as React.FC<OwnProps>

export default NivosRTE
